import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Upload, Download, Search, Trash2, Pencil,
  X, CheckCircle2, FileText, RefreshCw
} from "lucide-react";

const API = `${window.location.origin}/api`;

const fmtPrice = (v) => {
  if (!v && v !== 0) return "-";
  try { return new Intl.NumberFormat("tr-TR").format(v) + " ₺"; }
  catch { return String(v); }
};

const EMPTY_FORM = {
  brand: "", model_name: "", fabrika_nakit: "", liste_fiyati: "",
  ucar_nakit: "", ucar_taksitli: "", taksit_3: "", taksit_6: "",
  sahibinden: "", note: "",
};

function numOrNull(v) {
  const n = parseFloat(String(v).replace(",", "."));
  return isNaN(n) || n <= 0 ? null : n;
}

function ItemModal({ item, onSave, onClose }) {
  const [form, setForm] = useState(item ? {
    brand: item.brand || "", model_name: item.model_name || "",
    fabrika_nakit: item.fabrika_nakit ?? "",
    liste_fiyati: item.liste_fiyati ?? "",
    ucar_nakit: item.ucar_nakit ?? "",
    ucar_taksitli: item.ucar_taksitli ?? "",
    taksit_3: item.taksit_3 ?? "", taksit_6: item.taksit_6 ?? "",
    sahibinden: item.sahibinden ?? "", note: item.note || "",
  } : { ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.brand.trim() || !form.model_name.trim()) {
      toast.error("Marka ve Model Adı zorunludur");
      return;
    }
    setSaving(true);
    try {
      await onSave({
        brand: form.brand.trim(),
        model_name: form.model_name.trim(),
        fabrika_nakit: numOrNull(form.fabrika_nakit),
        liste_fiyati: numOrNull(form.liste_fiyati),
        ucar_nakit: numOrNull(form.ucar_nakit),
        ucar_taksitli: numOrNull(form.ucar_taksitli),
        taksit_3: numOrNull(form.taksit_3),
        taksit_6: numOrNull(form.taksit_6),
        sahibinden: numOrNull(form.sahibinden),
        note: form.note.trim() || null,
      });
    } finally { setSaving(false); }
  };

  const fields = [
    { key: "brand", label: "Marka*", placeholder: "KUBA", type: "text" },
    { key: "model_name", label: "Model Adı*", placeholder: "Newlight 125", type: "text" },
    { key: "fabrika_nakit", label: "Fabrika Nakit (₺)", placeholder: "0", type: "number" },
    { key: "liste_fiyati", label: "Fabrika Taksitli Liste Fiyatı (₺)", placeholder: "0", type: "number" },
    { key: "ucar_nakit", label: "Uçar Motor Özel Nakit Fiyatı (₺)", placeholder: "0", type: "number" },
    { key: "ucar_taksitli", label: "Uçar Motor Özel 8-12 Taksitli Fiyatı (₺)", placeholder: "0", type: "number" },
    { key: "taksit_3", label: "Uçar Motor Özel 3 Taksit Fiyatı (₺)", placeholder: "0", type: "number" },
    { key: "taksit_6", label: "Uçar Motor Özel 6 Taksit Fiyatı (₺)", placeholder: "0", type: "number" },
    { key: "sahibinden", label: "Sahibinden (₺)", placeholder: "0", type: "number" },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto animate-fade-in-up">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="text-lg font-bold uppercase text-gray-900"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
            {item ? "Model Düzenle" : "Yeni Model Ekle"}
          </h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5">
          <div className="grid grid-cols-2 gap-4">
            {fields.map(({ key, label, placeholder, type }) => (
              <div key={key} className={key === "brand" || key === "model_name" ? "col-span-2" : ""}>
                <label className="block text-xs font-bold uppercase text-gray-500 tracking-wider mb-1">
                  {label}
                </label>
                <input
                  data-testid={`item-form-${key}`}
                  type={type}
                  value={form[key]}
                  onChange={handle(key)}
                  placeholder={placeholder}
                  min={type === "number" ? "0" : undefined}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#DC2626] focus:border-transparent"
                />
              </div>
            ))}
            <div className="col-span-2">
              <label className="block text-xs font-bold uppercase text-gray-500 tracking-wider mb-1">Not</label>
              <input
                data-testid="item-form-note"
                type="text"
                value={form.note}
                onChange={handle("note")}
                placeholder="Peşin fiyatına 3 taksit"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#DC2626] focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-5">
            <button
              data-testid="item-form-save-btn"
              type="submit"
              disabled={saving}
              className="flex-1 py-2.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-bold rounded-md transition-colors disabled:opacity-50"
            >
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
            <button type="button" onClick={onClose}
              className="px-4 py-2.5 border border-gray-300 text-gray-600 text-sm rounded-md hover:bg-gray-50">
              İptal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ImportModal({ listId, headers, onDone, onClose }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [brand, setBrand] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [dragging, setDragging] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["csv", "xlsx", "xls"].includes(ext)) {
      toast.error("Sadece CSV veya Excel (.xlsx, .xls) dosyası yükleyin");
      return;
    }
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    handleFile(f);
  };

  const handleImport = async () => {
    if (!file) { toast.error("Dosya seçin"); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("default_brand", brand.trim());
      const res = await axios.post(`${API}/price-lists/${listId}/import`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      toast.success(`${res.data.imported} model içe aktarıldı`);
      onDone();
    } catch (err) {
      const detail = err.response?.data?.detail || "İçe aktarma başarısız";
      toast.error(detail);
    } finally { setImporting(false); }
  };

  const downloadTemplate = () => {
    const headers = ["Marka", "Model Adı", "Fabrika Nakit", "Fabrika Taksitli Liste Fiyatı", "Uçar Motor Özel Nakit Fiyatı", "Uçar Motor Özel 8-12 Taksitli Fiyatı", "Uçar Motor Özel 3 Taksit Fiyatı", "Uçar Motor Özel 6 Taksit Fiyatı", "Sahibinden", "Not"];
    const example = ["KUBA", "Newlight 125", "73603", "73603", "67000", "70000", "67000", "71500", "", "Dev Nakit İndirimi"];
    const csv = "\uFEFF" + headers.join(";") + "\n" + example.join(";");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "fiyat_listesi_sablonu.csv";
    a.click();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md animate-fade-in-up">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="text-lg font-bold uppercase text-gray-900"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
            Toplu İçe Aktar
          </h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {!result ? (
            <>
              {/* Drop zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  dragging ? "border-[#DC2626] bg-red-50" : "border-gray-300 hover:border-gray-400"
                }`}
              >
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
                  onChange={(e) => handleFile(e.target.files[0])} data-testid="import-file-input" />
                <Upload className={`w-8 h-8 mx-auto mb-2 ${dragging ? "text-[#DC2626]" : "text-gray-400"}`} />
                {file ? (
                  <p className="text-sm font-medium text-gray-700">{file.name}</p>
                ) : (
                  <p className="text-sm text-gray-500">Excel (.xlsx) veya CSV dosyası sürükleyin ya da tıklayın</p>
                )}
                <p className="text-xs text-gray-400 mt-1">.xlsx (önerilen), .xls, .csv</p>
              </div>

              {/* Default brand */}
              <div>
                <label className="block text-xs font-bold uppercase text-gray-500 tracking-wider mb-1">
                  Varsayılan Marka (Marka sütunu yoksa)
                </label>
                <input
                  data-testid="import-brand-input"
                  type="text"
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  placeholder="KUBA"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#DC2626] focus:border-transparent"
                />
              </div>

              <button
                onClick={downloadTemplate}
                className="flex items-center gap-2 text-xs text-[#DC2626] hover:underline"
              >
                <FileText className="w-3 h-3" /> CSV Şablonunu İndir
              </button>

              <div className="flex gap-3">
                <button
                  data-testid="import-confirm-btn"
                  onClick={handleImport}
                  disabled={importing || !file}
                  className="flex-1 py-2.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-bold rounded-md disabled:opacity-50 transition-colors"
                >
                  {importing ? <span className="flex items-center justify-center gap-2"><RefreshCw className="w-3.5 h-3.5 animate-spin" /> İçe Aktarılıyor...</span> : "İçe Aktar"}
                </button>
                <button onClick={onClose}
                  className="px-4 py-2.5 border border-gray-300 text-gray-600 text-sm rounded-md hover:bg-gray-50">
                  İptal
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-lg font-bold text-gray-800">İçe Aktarma Tamamlandı</p>
              <div className="mt-3 space-y-1 text-sm text-gray-600">
                <p>Aktarılan: <strong className="text-green-600">{result.imported}</strong></p>
                <p>Atlanan: <strong className="text-gray-400">{result.skipped}</strong></p>
                <p>Toplam satır: <strong>{result.total}</strong></p>
              </div>
              <button onClick={onClose}
                className="mt-4 px-6 py-2 bg-[#DC2626] text-white text-sm font-bold rounded-md hover:bg-[#B91C1C]">
                Kapat
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PriceItemsPage() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  const [priceList, setPriceList] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const LIMIT = 50;
  const totalPages = Math.ceil(total / LIMIT);

  const fetchPL = async () => {
    try {
      const res = await axios.get(`${API}/price-lists/${id}`, { headers });
      setPriceList(res.data);
    } catch { toast.error("Liste bilgisi yüklenemedi"); }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/price-lists/${id}/items`, {
        headers, params: { page, limit: LIMIT, search },
      });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch { toast.error("Modeller yüklenemedi"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPL(); }, [id]); // eslint-disable-line
  useEffect(() => { fetchItems(); }, [id, page, search]); // eslint-disable-line

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleAdd = async (data) => {
    try {
      await axios.post(`${API}/price-lists/${id}/items`, data, { headers });
      toast.success("Model eklendi");
      setShowAddModal(false);
      fetchItems(); fetchPL();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ekleme başarısız");
      throw err;
    }
  };

  const handleEdit = async (data) => {
    try {
      await axios.put(`${API}/price-lists/${id}/items/${editItem.id}`, data, { headers });
      toast.success("Model güncellendi");
      setEditItem(null);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Güncelleme başarısız");
      throw err;
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/price-lists/${id}/items/${deleteId}`, { headers });
      toast.success("Model silindi");
      setDeleteId(null);
      fetchItems(); fetchPL();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silme başarısız");
    } finally { setDeleting(false); }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/price-lists/${id}/export`, {
        headers, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      }));
      const a = document.createElement("a");
      a.href = url;
      const name = priceList?.name?.replace(/\s+/g, "_") || "fiyat_listesi";
      a.download = `${name}_export.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Excel dosyası indirildi");
    } catch { toast.error("Dışa aktarma başarısız"); }
  };

  return (
    <Layout>
      <div className="p-8">
        {/* Breadcrumb */}
        <button
          onClick={() => navigate("/fiyat-listeleri")}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-5 transition-colors"
          data-testid="back-to-lists-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Fiyat Listelerine Dön
        </button>

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold uppercase text-gray-900 tracking-tight"
                style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                {priceList?.name || "Yükleniyor..."}
              </h1>
              {priceList?.status === "ACTIVE" && (
                <span className="text-xs font-bold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-sm">
                  AKTİF
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {total} model {search && `("${search}" için)`}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="import-btn" onClick={() => setShowImport(true)}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 text-xs font-bold uppercase rounded-md hover:bg-gray-50 transition-colors">
              <Upload className="w-3.5 h-3.5" /> Excel İçe Aktar
            </button>
            <button data-testid="export-btn" onClick={handleExport}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 text-xs font-bold uppercase rounded-md hover:bg-gray-50 transition-colors">
              <Download className="w-3.5 h-3.5" /> Excel Dışa Aktar
            </button>
            <button data-testid="add-item-btn" onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-3 py-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs font-bold uppercase rounded-md transition-colors">
              <Plus className="w-3.5 h-3.5" /> Model Ekle
            </button>
          </div>
        </div>

        {/* Search */}
        <form onSubmit={handleSearchSubmit} className="flex gap-2 mb-5">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              data-testid="item-search-input"
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Model adı veya marka ara..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#DC2626] focus:border-transparent"
            />
          </div>
          <button type="submit" data-testid="search-btn"
            className="px-4 py-2 bg-gray-800 text-white text-sm font-medium rounded-md hover:bg-gray-900 transition-colors">
            Ara
          </button>
          {search && (
            <button type="button" onClick={() => { setSearch(""); setSearchInput(""); setPage(1); }}
              className="px-3 py-2 border border-gray-300 text-gray-500 text-sm rounded-md hover:bg-gray-50">
              <X className="w-4 h-4" />
            </button>
          )}
        </form>

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-8 h-8 border-2 border-[#DC2626] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-gray-400">
              <FileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">{search ? "Arama sonucu bulunamadı" : "Henüz model yok. Model ekleyin veya içe aktarın."}</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="items-table">
                  <thead>
                    <tr className="bg-gray-50 text-xs font-bold uppercase tracking-wider text-gray-500 border-b">
                      <th className="px-4 py-3 text-left">Marka</th>
                      <th className="px-4 py-3 text-left">Model</th>
                      <th className="px-4 py-3 text-left text-gray-400">Key</th>
                      <th className="px-4 py-3 text-right leading-tight">Fabrika Taksitli<br/>Liste Fiyatı</th>
                      <th className="px-4 py-3 text-right leading-tight">Uçar Motor<br/>Özel Nakit</th>
                      <th className="px-4 py-3 text-right leading-tight">Uçar Motor<br/>8-12 Taksit</th>
                      <th className="px-4 py-3 text-right leading-tight">Uçar Motor<br/>3 Taksit</th>
                      <th className="px-4 py-3 text-right leading-tight">Uçar Motor<br/>6 Taksit</th>
                      <th className="px-4 py-3 text-left">Not</th>
                      <th className="px-4 py-3 text-right">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <span className="text-xs font-bold text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{item.brand}</span>
                        </td>
                        <td className="px-4 py-3 font-semibold text-gray-800">{item.model_name}</td>
                        <td className="px-4 py-3 text-xs text-gray-400 font-mono">{item.model_key}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm text-gray-600">{fmtPrice(item.liste_fiyati)}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm font-semibold text-gray-800">{fmtPrice(item.ucar_nakit)}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm text-purple-600">{fmtPrice(item.ucar_taksitli)}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm text-blue-600">{fmtPrice(item.taksit_3)}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm text-blue-600">{fmtPrice(item.taksit_6)}</td>
                        <td className="px-4 py-3 text-xs text-gray-500 max-w-[140px] truncate" title={item.note}>{item.note || "-"}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button data-testid={`edit-item-btn-${item.id}`}
                              onClick={() => setEditItem(item)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button data-testid={`delete-item-btn-${item.id}`}
                              onClick={() => setDeleteId(item.id)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500">
                    {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} / {total} model
                  </p>
                  <div className="flex items-center gap-1">
                    <button data-testid="prev-page-btn"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-3 py-1.5 text-xs border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50">
                      Önceki
                    </button>
                    <span className="px-3 py-1.5 text-xs text-gray-600">{page} / {totalPages}</span>
                    <button data-testid="next-page-btn"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      className="px-3 py-1.5 text-xs border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50">
                      Sonraki
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modals */}
        {showAddModal && <ItemModal onSave={handleAdd} onClose={() => setShowAddModal(false)} />}
        {editItem && <ItemModal item={editItem} onSave={handleEdit} onClose={() => setEditItem(null)} />}
        {showImport && (
          <ImportModal
            listId={id} headers={headers}
            onDone={() => { fetchItems(); fetchPL(); }}
            onClose={() => setShowImport(false)}
          />
        )}

        {/* Delete confirm */}
        {deleteId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full animate-fade-in-up">
              <h3 className="text-lg font-bold text-gray-900 mb-2"
                style={{ fontFamily: "Barlow Condensed, sans-serif" }}>Modeli Sil?</h3>
              <p className="text-sm text-gray-600 mb-5">Bu model kalıcı olarak silinecek.</p>
              <div className="flex gap-3">
                <button data-testid="confirm-delete-item-btn"
                  onClick={handleDelete} disabled={deleting}
                  className="flex-1 py-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-bold rounded-md disabled:opacity-50">
                  {deleting ? "..." : "Sil"}
                </button>
                <button onClick={() => setDeleteId(null)}
                  className="flex-1 py-2 border border-gray-300 text-gray-600 text-sm rounded-md hover:bg-gray-50">
                  İptal
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
